import { Box, Card, RadioButton, Title } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface BudgetExceededOptionSelectorProps {
  currency: string
  budgetLimit: number
  totalAllocatedAmountCents: number
  newAllocationValueCents: number
  numberOfEmployees: number
  setVisible: (display: boolean) => void
  visible: boolean
  setSelectorResult: (value: BudgetExceededOptionResultType) => void
  setAllocationValue: (value: number) => void
  clearErrors: () => void
}

export type BudgetExceededOptionResultType = {
  increaseLimit: number
  tryAnotherAllocation: number
  type: 'increaseLimit' | 'tryAnotherAllocation' | ''
}

const BudgetExceededOptionSelector = ({
  budgetLimit,
  currency,
  newAllocationValueCents,
  numberOfEmployees,
  totalAllocatedAmountCents,
  visible,
  setVisible,
  setSelectorResult,
  setAllocationValue,
  clearErrors,
}: BudgetExceededOptionSelectorProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const validNewAllocationValue = isNaN(newAllocationValueCents) ? 0 : newAllocationValueCents
  const validNumberOfEmployees = numberOfEmployees || 1
  const budgetLimitNum = Number(budgetLimit)
  const totalAllocatedNum = Number(totalAllocatedAmountCents)

  const suggestedBudgetLimit = Math.round(
    validNewAllocationValue * validNumberOfEmployees + totalAllocatedNum
  )
  const suggestedNewAllocationAmount = Math.floor(
    (budgetLimitNum - totalAllocatedNum) / validNumberOfEmployees
  )

  const result: BudgetExceededOptionResultType = {
    increaseLimit: suggestedBudgetLimit,
    tryAnotherAllocation: suggestedNewAllocationAmount,
    type: '',
  }

  if (!visible) return null

  return (
    <Box
      gap="s24"
      paddingTop="s24"
      border={{ top: { color: 'grey400', style: 'solid', width: 's1' } }}
    >
      <Title
        variant="500"
        title={i18n.t('budgets.budget_exceeded_option_selector.title')}
        description={i18n.t('budgets.budget_exceeded_option_selector.description')}
      />

      <Card
        onClick={() => {
          setVisible(false)
          setSelectorResult({ ...result, type: 'increaseLimit' })
          clearErrors()
        }}
      >
        <Card.Title
          action={
            <RadioButton
              key="options"
              checked={result.type == 'increaseLimit'}
              size="medium"
              onChange={() => {}}
              value={'increaseLimit'}
            />
          }
        >
          {i18n.t('budgets.budget_exceeded_option_selector.card_1.title')}
        </Card.Title>
        <Card.Description>
          {i18n.t('budgets.budget_exceeded_option_selector.card_1.description', {
            previousLimit: amountWithCurrency(Math.round(budgetLimitNum), currency, locale),
            newLimit: amountWithCurrency(Math.round(suggestedBudgetLimit), currency, locale),
          })}
        </Card.Description>
      </Card>

      {!isNaN(suggestedNewAllocationAmount) && suggestedNewAllocationAmount > 0 && (
        <Card
          onClick={() => {
            setVisible(false)
            setSelectorResult({ ...result, type: 'tryAnotherAllocation' })
            setAllocationValue(suggestedNewAllocationAmount / 100)
            clearErrors()
          }}
        >
          <Card.Title
            action={
              <RadioButton
                key="options"
                checked={result.type == 'tryAnotherAllocation'}
                size="medium"
                onChange={() => {}}
                value={'tryAnotherAllocation'}
              />
            }
          >
            {i18n.t('budgets.budget_exceeded_option_selector.card_2.title')}
          </Card.Title>
          <Card.Description>
            {i18n.t('budgets.budget_exceeded_option_selector.card_2.description', {
              newAmount: amountWithCurrency(
                Math.round(suggestedNewAllocationAmount),
                currency,
                locale
              ),
            })}
          </Card.Description>
        </Card>
      )}
    </Box>
  )
}

export default BudgetExceededOptionSelector
